<template>
  <div class="editor-wrapper">
    <div id="back-settings-btn-wrapper">
      <div id="back-btn">
        <span v-on:click="(e) => $emit('onBackToArticlesClick', e)" class="fas fa-long-arrow-left" /> {{ $t("back") }}
        <span>({{backToArticleListShortcut}})</span>
      </div>
      <div id="settings-btn">
        <span v-on:click="(e) => toggleShortcutOptionsModal(true, e)" class="fa fa-cog" /> {{ $t("options") }}
        <span>({{openOptionsModalShortcut}})</span>
      </div>
    </div>
    <div id="editor-actions">
      <a :href="insightUrl" class="insight-url" :class="{disabled: !insightUrl}" target="_blank">
        <i class="fas fa-external-link-alt"></i>
        <span>{{$t("insight.view")}}</span>
      </a>
      <span v-if="!isClipIrrelevant" v-on:click="(e) => $emit('flagAsIrrelevant', e)" class="flag-irrelevant">
        <i class="fas fa-times"></i>
        <span>{{$t("irrelevant")}}</span>
      </span>
      <span v-else class="flagged-irrelevant">
        <i class="fas fa-times"></i>
        <span>{{$t("flagged_irrelevant")}}</span>
      </span>
      <span v-if="aiCodingData" @click="() => $emit('showAICodingHistory', aiCodingData)">
        <i class="fas fa-history"></i> {{ $t('ai_coding_history.button') }}
      </span>
      <span :class="'relevance-category-label '+relevancyLabel.cssClass" v-tooltip="relevancyLabel.tooltip">
        <i :class="relevancyLabel.icon"></i>
        <span>{{relevancyLabel.title}}</span>
      </span>
      <span v-if="isTranslationAvailable" v-on:click="(e) => $emit('toggleShowTranslation', !showTranslation, e)" class="insight-url">
        <i class="fas fa-eye"></i>
        <span>{{showTranslation ? $t("translation.view_original_text") : $t("translation.view_translation")}}</span>
      </span>
    </div>
    <div id="editor-content" v-if="analysisArticle" v-on:mouseup="toggleCopyQuoteIcon">
      <app-article ref="article"
                   :analysisArticle="analysisArticle"
                   :articleContent="articleContent"
                   :articleHeadline="articleHeadline"
                   :currentParagraphIndex="currentParagraphIndex"
                   :noOfSelectedSentences="noOfSelectedSentences"
                   :showTranslation="showTranslation"
      />
    </div>
    <div v-if="activeTab === CODING_TABS.SOURCES && activeSource && activeQuoteIndex !== -1"
         id="copy-active-quote"
         :class="{'icon-btn': true, 'icon-quote': true, 'fa fa-quote-left': true, 'display-none': !displayCopyIcon }"
         v-on:click="updateSourceMentionQuote"
    />

    <app-shortcut-options-modal id="shortcut-modal"
                                v-if="showShortcutOptionsModal"
                                @closeModal="toggleShortcutOptionsModal"
    />
  </div>
</template>

<script>
import { insightExternalUrl } from '../../helpers/articles';

import Article from './article';
import ShortcutOptionsModal from './shortcut_options_modal';

import { CODING_TABS } from '../../config/const';
import SHORTCUTS from '../../config/shortcut-keys';
import editor from '../../helpers/editor';

export default {
  props: {
    analysisArticle:  {
      type: Object
    },
    articleContent: {
      type: Array,
      default: () => []
    },
    articleHeadline: {
      type: String,
      default: ''
    },
    showTranslation: {
      type: Boolean,
      default: false
    },
    currentParagraphIndex: {
      type: Number,
      required: true
    },
    noOfSelectedSentences: {
      type: Number,
      required: true
    },
    activeTab: {
      type: String,
      default: CODING_TABS.EDITOR
    },
    activeSource: {
      type: Object,
      default: undefined
    },
    activeQuoteIndex: {
      type: Number,
      default: -1
    },
    isClipIrrelevant: {
      type: Boolean,
      default: false
    },
    relevancySetting:{
      type: Object
    },
    relevancyLoading: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false  
    }
  },
  components: {
    'app-article': Article,
    'app-shortcut-options-modal': ShortcutOptionsModal
  },
  computed: {
    aiCodingData () {
      return this.analysisArticle.data && this.analysisArticle.data.data && this.analysisArticle.data.data.ai_coding;
    },
    backToArticleListShortcut () {
      return editor.getShortcutAsString(SHORTCUTS.shortcuts.back_to_article_list, '+');
    },
    openOptionsModalShortcut () {
      return editor.getShortcutAsString(SHORTCUTS.shortcuts.options, '+');
    },
    insightUrl () {
      if (
        this.analysisArticle.data &&
        this.analysisArticle.data.data.article_synced &&
        this.analysisArticle.data.data.coding_uid
      ) {
        return insightExternalUrl(this.analysisArticle.data.data.coding_uid);
      }
      return false;
    },
    isTranslationAvailable () {
       return this.analysisArticle.data &&
        this.analysisArticle.data.data.article_synced &&
        this.analysisArticle.data.data.translated_headline &&
        this.analysisArticle.data.data.translated_content
    },
    relevancyLabel(){
      let score = this.analysisArticle.data?.data?.relevancy_score || 'N/A';
      let mapping = this.relevancySetting?.settings?.mapping;
      if(this.loading || this.relevancyLoading) return {};
      
      if(score >= mapping?.high_upper_limit){
        return {cssClass: 'bg-success-dark', title: this.$t("most_relevant"), tooltip: this.$t("score")+': '+score }
      } else if(score >= mapping?.medium_upper_limit){
        return {cssClass: 'bg-info-dark', title: this.$t("high"), tooltip: this.$t("score")+': '+score}
      } else if(score >= mapping?.low_upper_limit){
        return {cssClass: 'bg-yellow-dark', title: this.$t("medium"), tooltip: this.$t("score")+': '+score}
      } 
      return {cssClass: 'bg-light-gray', title: this.$t("low"), tooltip: this.$t("score")+': '+score}
    }
  },
  methods: {
    toggleCopyQuoteIcon (e) {
      if(this.activeTab === CODING_TABS.SOURCES && window.getSelection().toString().trim() !== '') {
        this.displayCopyIcon = true;
        this.selectedText = window.getSelection().toString();

        const top = e.clientY + e.target.closest('.editor-wrapper').scrollTop;
        const left = e.clientX;

        this.repositionCopyQuoteIcon(top, left);
      }
      else if (this.displayCopyIcon) {
        this.displayCopyIcon = false;
      }
    },
    repositionCopyQuoteIcon(top, left) {
      let btnCopyQuote = document.getElementById('copy-active-quote');
      if (btnCopyQuote) {
        btnCopyQuote.style.top = `${top}px`;
        btnCopyQuote.style.left = `${left}px`;
      }
    },
    updateSourceMentionQuote () {
      this.$emit('updateSourceMentionQuote', this.selectedText);
      this.displayCopyIcon = false;
      this.selectedText = '';
    },
    toggleShortcutOptionsModal (open, e) {
      if(e) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.showShortcutOptionsModal = open;
    }
  },
  data() {
    return {
      CODING_TABS: CODING_TABS,
      displayCopyIcon: false,
      selectedText: '',
      showShortcutOptionsModal: false
    }
  }
}
</script>

<style lang="scss">
@import "../../styles/components/coding-ui/content";
</style>